/**
 * Record-type terminology — the ONE place audition-vs-competition wording is
 * decided (organizer management UI redesign, see
 * docs/wip/active/WIP_redesigning_organizer_management_ui_2026_06_04.md).
 *
 * Competitions are `auditions` rows with `is_competition = true` (single
 * entity, single editor — Critical Rule #8). The UI forks WORDING only:
 * "Edit audition" → "Edit competition", "Applications" → "Entries",
 * "Requirements" → "Eligibility", etc.
 *
 * Mechanism: i18next's `context` feature. Base keys hold the audition
 * wording; only keys that fork carry a `_competition` twin in the locale
 * files (`apps/web/public/locales/<locale>/auditions.json`, `record.*`
 * tree). Resolving with `context: 'competition'` picks the twin and falls
 * back to the base key when no twin exists — so non-forking keys are never
 * duplicated.
 *
 * Usage:
 *   const type = recordTypeOf(audition);
 *   t(`auditions:record.${token}`, { context: recordContext(type) })
 *   <Trans i18nKey="auditions:record.publish"
 *          tOptions={{ context: recordContext(type) }} />
 *
 * Components must NEVER branch on `is_competition` for wording — they pass
 * the context and i18next does the switch. Section/tab VISIBILITY branching
 * lives in `components/audition-form/tab-registry.ts`, not here.
 */

export type RecordType = 'audition' | 'competition';

/** Resolve the record type from any audition-shaped row. */
export function recordTypeOf(audition: {
  is_competition: boolean | null;
}): RecordType {
  return audition.is_competition ? 'competition' : 'audition';
}

/**
 * The i18next `context` value for a record type. `undefined` for auditions
 * (base keys); `'competition'` selects the `_competition` key variants.
 */
export function recordContext(type: RecordType): 'competition' | undefined {
  return type === 'competition' ? 'competition' : undefined;
}

/**
 * The terminology tokens that FORK between record types. Every token below
 * exists in the `record.*` tree of `auditions.json` as BOTH the base key and
 * a `_competition` variant — locked by record-terminology.test.ts so a
 * missing twin can never ship silently.
 *
 * Non-forking labels (field names, helper text, dates UI) are ordinary keys
 * elsewhere in the namespace and deliberately NOT listed here.
 */
export const RECORD_TERMINOLOGY_TOKENS = [
  'kicker',
  'title',
  'editTitle',
  'publish',
  'delete',
  'deleteConfirm',
  'untitled',
  'applications',
  'applicant',
  'applicationPeriod',
  'applicationFee',
  'requirements',
  'apply',
  'viewPublicPage',
  'createFirst',
  'saved',
  'published',
  'deleted',
] as const;

export type RecordTerminologyToken = (typeof RECORD_TERMINOLOGY_TOKENS)[number];

/**
 * Fully-qualified i18n key for a terminology token (for callsites that
 * build keys programmatically, e.g. server components passing keys to
 * <Trans>). The record-type fork still happens via `context`, never by
 * suffixing the key by hand — hand-suffixed keys skip i18next's fallback
 * and render raw when a twin is missing.
 */
export function recordTerminologyKey(token: RecordTerminologyToken): string {
  return `auditions:record.${token}`;
}
